'use client';

import type { ListWelloPolicyCodeAllDto } from '__generated__/api/unauth';
import { Button, Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgCheck, SvgChevrondown, SvgTarget2 } from '@common/icon';
import { cn, CustomError, isEnumValue } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { EXCEPTED_CODE, EXCEPTED_HIDDEN_CODE, UnauthQueryOptions } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import type { SEARCH_PARAMS_TYPE } from '@/constants';
import { GEOLOCATION_VIEW_TYPE } from '@/constants/geo';
import { DEFAULT_LOCATION, useLocation } from '@/hooks/useLocation';

interface SelectedRegion {
  mainRegion: ListWelloPolicyCodeAllDto;
  subRegion: ListWelloPolicyCodeAllDto;
}

interface FeedRegionBarProps {
  value?: SEARCH_PARAMS_TYPE.SELECTED_REGION;
  onChange?: (value: SelectedRegion) => void;
}

export const FeedRegionBar = ({ value, onChange }: FeedRegionBarProps) => {
  const policyFitMetaDataQuery = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/wello-policy-code/all'](),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ context }) => {
      const { contents } = context ?? {};

      if (!contents)
        throw new CustomError({ return_message: '정보가 없습니다.' });

      const mainRegion: ListWelloPolicyCodeAllDto[] = [];
      const subRegionByParent = new Map<string, ListWelloPolicyCodeAllDto[]>();
      const subRegionByCode = new Map<string, ListWelloPolicyCodeAllDto>();

      contents.forEach((content) => {
        const { code } = content;
        let { level } = content;

        if (!code || !level) return;

        const isHidden =
          code.includes('99') && !isEnumValue(EXCEPTED_HIDDEN_CODE, code);

        if (isHidden) return;

        if (code.includes(EXCEPTED_CODE.GENDER)) return;

        if (code.startsWith('C14')) level = level - 1;

        if (!code.includes(EXCEPTED_CODE.REGION)) return;

        if (level === 2) {
          mainRegion.push(content);

          return;
        }

        if (level === 3) {
          subRegionByCode.set(code, content);

          const parentCode = content.parentCode ?? '';
          const siblings = subRegionByParent.get(parentCode);

          if (siblings) {
            siblings.push(content);
          } else {
            subRegionByParent.set(parentCode, [content]);
          }
        }
      });

      subRegionByParent.forEach((list) => {
        list.sort((a, b) => {
          if (typeof a.value !== 'string' || typeof b.value !== 'string')
            return 0;

          return a.value.localeCompare(b.value);
        });
      });

      return { mainRegion, subRegionByParent, subRegionByCode };
    },
  });

  const { mainRegion, subRegionByParent, subRegionByCode } =
    policyFitMetaDataQuery.data ?? {};

  const { refetch: getLocation, isLoading: isLocationLoading } = useLocation({
    geolocationViewType: GEOLOCATION_VIEW_TYPE.HOME_TOWN_NEWS,
  });

  const { toast } = useToast();

  const [selectedValue, setSelectedValue] =
    useState<SEARCH_PARAMS_TYPE.SELECTED_REGION>();

  const isMetaLoading = policyFitMetaDataQuery.isLoading;
  const drawerOpened = !!selectedValue;

  const selectedRegionText = value?.subRegionCode
    ? (subRegionByCode?.get(value.subRegionCode)?.value ?? '')
    : '';

  const subRegionList = selectedValue?.mainRegionCode
    ? subRegionByParent?.get(selectedValue.mainRegionCode)
    : undefined;

  const handleOpenDrawer = () => {
    setSelectedValue(
      value?.mainRegionCode
        ? value
        : { mainRegionCode: DEFAULT_LOCATION.codeRegion },
    );
  };

  const handleSelectCurrentLocation = async () => {
    const location = await getLocation();
    const { codeRegion, codeSubRegion } = location.data ?? {};

    const foundMain = mainRegion?.find(({ code }) => code === codeRegion);
    const foundSub = codeSubRegion
      ? subRegionByCode?.get(codeSubRegion)
      : undefined;

    if (!foundMain || !foundSub)
      throw new CustomError({
        return_message: '위치 정보를 가져오는데 실패했습니다.',
      });

    onChange?.({ mainRegion: foundMain, subRegion: foundSub });

    toast({ message: '현재 위치가 설정되었어요.' });
  };

  const handleConfirm = () => {
    const foundMain = mainRegion?.find(
      ({ code }) => code === selectedValue?.mainRegionCode,
    );
    const foundSub = selectedValue?.subRegionCode
      ? subRegionByCode?.get(selectedValue.subRegionCode)
      : undefined;

    if (!foundMain || !foundSub)
      throw new CustomError({
        return_message: '지역 정보를 가져오지 못했어요.',
      });

    onChange?.({ mainRegion: foundMain, subRegion: foundSub });
    setSelectedValue(undefined);
  };

  return (
    <div
      aria-label="지역 선택"
      role="group"
      className="flex w-full items-center justify-between border-b-1 border-b-Gray-100 bg-Gray-white px-16 pb-8 pt-4 xl:border-b-0 xl:bg-transparent"
    >
      <button
        aria-expanded={drawerOpened}
        aria-haspopup="dialog"
        aria-label={
          selectedRegionText
            ? `지역 선택: 현재 ${selectedRegionText}`
            : '지역 선택'
        }
        className="flex items-center rounded-xsmall transition-colors duration-150 hover:bg-Gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 disabled:opacity-50"
        disabled={isMetaLoading}
        onClick={handleOpenDrawer}
      >
        <span className="truncate font-600 text-Gray-700 typo-body4">
          {selectedRegionText || '지역'}
        </span>
        <span
          aria-hidden="true"
          className="flex size-24 items-center justify-center"
        >
          <SvgChevrondown
            className="size-16 text-Gray-700"
            name="Arrow-Down-4"
          />
        </span>
      </button>
      <button
        aria-busy={isLocationLoading}
        aria-label="현재 위치로 지역 설정"
        className="flex items-center gap-4 rounded-xsmall px-4 py-2 font-500 text-Gray-700 transition-colors duration-150 typo-body4 hover:bg-Gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 disabled:opacity-50"
        disabled={isLocationLoading || isMetaLoading}
        onClick={handleSelectCurrentLocation}
      >
        <span
          aria-hidden="true"
          className="flex size-14 items-center justify-center"
        >
          {isLocationLoading ? (
            <Spinner size={14} />
          ) : (
            <SvgTarget2 className="size-14" name="위치 정보 업데이트" />
          )}
        </span>
        현위치
      </button>
      <ResponsiveDialog
        drawerType="bottom"
        modalHeader="관심 지역을 선택해 주세요"
        opened={drawerOpened}
        title="관심 지역을 선택해 주세요"
        onClose={() => setSelectedValue(undefined)}
      >
        <div className="flex max-h-300">
          <ul
            aria-label="시/도"
            className="flex-1 overflow-auto border-r-1 border-Gray-300 px-5 hide-scrollbar webkit-scroll-touch"
          >
            {mainRegion?.map(({ value: mainValue, code }) => {
              if (!code || !mainValue) return null;

              const isSelected = code === selectedValue?.mainRegionCode;

              return (
                <li key={code}>
                  <button
                    aria-pressed={isSelected}
                    className={cn(
                      'flex w-full cursor-pointer items-center justify-between text-nowrap rounded-xsmall px-12 py-8 font-400 text-Gray-800 transition-colors duration-150 typo-body3 hover:bg-Gray-50 focus-visible:bg-Gray-50 focus-visible:outline-none',
                      { 'font-600 text-B-500': isSelected },
                    )}
                    onClick={() => {
                      if (isSelected) return;

                      setSelectedValue({ mainRegionCode: code });
                    }}
                  >
                    {mainValue}
                    {isSelected ? (
                      <SvgCheck
                        aria-hidden
                        className="size-16 stroke-B-500 text-B-500"
                        name="선택됨"
                        strokeWidth={1}
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <ul
            aria-label="시/군/구"
            className="flex-1 overflow-auto px-5 hide-scrollbar webkit-scroll-touch"
          >
            {subRegionList?.map(({ code, value: subValue }) => {
              if (!code || !subValue) return null;

              const isSelected = code === selectedValue?.subRegionCode;

              return (
                <li key={code}>
                  <button
                    aria-pressed={isSelected}
                    className={cn(
                      'flex w-full cursor-pointer items-center justify-between text-nowrap rounded-xsmall px-12 py-8 font-400 text-Gray-800 transition-colors duration-150 typo-body3 hover:bg-Gray-50 focus-visible:bg-Gray-50 focus-visible:outline-none',
                      { 'font-600 text-B-500': isSelected },
                    )}
                    onClick={() => {
                      if (isSelected) return;

                      setSelectedValue((prev) => ({
                        ...prev,
                        subRegionCode: code,
                      }));
                    }}
                  >
                    {subValue}
                    {isSelected ? (
                      <SvgCheck
                        aria-hidden
                        className="size-16 stroke-B-500 text-B-500"
                        name="선택됨"
                        strokeWidth={1}
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <footer className="mt-16 w-full pt-16">
          <Button
            className="w-full"
            disabled={
              !selectedValue?.mainRegionCode || !selectedValue?.subRegionCode
            }
            onClick={handleConfirm}
          >
            선택 완료
          </Button>
        </footer>
      </ResponsiveDialog>
    </div>
  );
};
